import { useEffect, useMemo, useState } from "react";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import SearchableSelect from "@/components/SearchableSelect";
import { Plus, CheckCircle, XCircle, Trash, Clock, ShoppingCart, X as XIcon, PencilSimple, Funnel, DownloadSimple } from "@phosphor-icons/react";

const URGENCY_STYLE = {
  bassa: "border-neutral-300 text-neutral-700 bg-neutral-50",
  media: "border-amber-400 text-amber-800 bg-amber-50",
  alta: "border-red-400 text-red-700 bg-red-50",
};
const STATUS_STYLE = {
  pending: "border-amber-400 text-amber-800 bg-amber-50",
  approved: "border-emerald-500 text-emerald-800 bg-emerald-50",
  rejected: "border-red-400 text-red-700 bg-red-50",
};
const STATUS_LABEL = { pending: "In attesa", approved: "Approvata", rejected: "Rifiutata" };
const eur = (v) => `€ ${Number(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const summarizeItems = (items) => {
  if (!items || items.length === 0) return "—";
  const first = items[0];
  const label = `${first.product_sku || "?"} — ${first.product_name || "?"}`;
  return items.length > 1 ? `${label} + ${items.length - 1} altri` : label;
};

export default function PurchaseRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [supplierFilter, setSupplierFilter] = useState("");
  const [requesterFilter, setRequesterFilter] = useState("");
  const [textFilter, setTextFilter] = useState("");
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [processing, setProcessing] = useState(false);

  const productOpts = useMemo(
    () => products.map((p) => ({
      value: p.id,
      label: `${p.sku} — ${p.name}`,
      sublabel: p.supplier_name ? `Fornitore: ${p.supplier_name}` : "",
    })),
    [products]
  );
  const supplierOpts = useMemo(
    () => [{ value: "", label: "— Usa fornitore del prodotto —" }, ...suppliers.map((s) => ({ value: s.id, label: s.name }))],
    [suppliers]
  );

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (urgencyFilter) params.urgency = urgencyFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const [r, p, s] = await Promise.all([
        api.get("/purchase-requests", { params }),
        products.length ? Promise.resolve({ data: products }) : api.get("/products"),
        suppliers.length ? Promise.resolve({ data: suppliers }) : api.get("/suppliers"),
      ]);
      setRows(r.data || []);
      if (!products.length) setProducts(p.data || []);
      if (!suppliers.length) setSuppliers(s.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statusFilter, urgencyFilter, dateFrom, dateTo]);

  const filteredRows = useMemo(() => {
    const q = textFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (supplierFilter) {
        const prodSupplier = (r.items || []).find((it) => it.product_id)?.product_id;
        const effective = r.supplier_id
          || (prodSupplier ? (products.find((p) => p.id === prodSupplier)?.supplier_id) : null);
        if (effective !== supplierFilter) return false;
      }
      if (requesterFilter && (r.created_by_id || "") !== requesterFilter) return false;
      if (!q) return true;
      const hay = [r.pr_number, r.notes, r.supplier_name, r.created_by_name, r.created_by,
        ...(r.items || []).flatMap((it) => [it.product_sku, it.product_name, it.product_manufacturer_code])]
        .filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, textFilter, supplierFilter, requesterFilter]);

  const filteredTotals = useMemo(() => ({
    count: filteredRows.length,
    quantity: filteredRows.reduce((a, r) => a + (r.total_quantity || 0), 0),
    amount: filteredRows.reduce((a, r) => a + (r.total_amount || 0), 0),
  }), [filteredRows]);

  const requesterOpts = useMemo(() => {
    const seen = new Map();
    rows.forEach((r) => { if (r.created_by_id) seen.set(r.created_by_id, r.created_by_name || r.created_by); });
    return [{ value: "", label: "Tutti i richiedenti" },
      ...Array.from(seen, ([value, label]) => ({ value, label }))];
  }, [rows]);
  const supplierFilterOpts = useMemo(() => [{ value: "", label: "Tutti i fornitori" },
    ...suppliers.map((s) => ({ value: s.id, label: s.name }))], [suppliers]);

  const resetFilters = () => {
    setStatusFilter(""); setUrgencyFilter(""); setDateFrom(""); setDateTo("");
    setSupplierFilter(""); setRequesterFilter(""); setTextFilter("");
  };

  const [exporting, setExporting] = useState(false);
  const exportXlsx = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      if (urgencyFilter) qs.set("urgency", urgencyFilter);
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      const t = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/purchase-requests/export?${qs.toString()}`, {
        credentials: "include", headers: t ? { Authorization: `Bearer ${t}` } : {},
      });
      if (!res.ok) throw new Error(await res.text());
      const url = window.URL.createObjectURL(await res.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `richieste-acquisto_${dateFrom || "inizio"}_${dateTo || "oggi"}.xlsx`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Richieste esportate");
    } catch (e) { toast.error(formatApiError(e) || "Errore durante l'export"); }
    finally { setExporting(false); }
  };

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/purchase-requests/${id}`); setDetail(data); setReviewNotes(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const review = async (action) => {
    if (!detail) return;
    setProcessing(true);
    try {
      const { data } = await api.post(`/purchase-requests/${detail.id}/${action}`,
        { review_notes: reviewNotes.trim() || null });
      toast.success(action === "approve" ? "Richiesta approvata" : "Richiesta rifiutata");
      setDetail(data);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setProcessing(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Eliminare la richiesta ${r.pr_number || r.id.slice(0, 8)}?`)) return;
    try { await api.delete(`/purchase-requests/${r.id}`); toast.success("Richiesta eliminata"); setDetail(null); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6 space-y-6">
      <PageHeader eyebrow="Approvvigionamento" title="Richieste di Acquisto"
        subtitle={isAdmin ? "Approva o rifiuta le richieste dei collaboratori" : "Le tue richieste di acquisto"} />

      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex gap-1 text-xs">
            {[
              { v: "", label: "Tutte" },
              { v: "pending", label: "In attesa" },
              { v: "approved", label: "Approvate" },
              { v: "rejected", label: "Rifiutate" },
            ].map((f) => (
              <button
                key={f.v}
                data-testid={`pr-filter-${f.v || "all"}`}
                onClick={() => setStatusFilter(f.v)}
                className={`px-3 py-1.5 border transition-colors ${statusFilter === f.v
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white border-neutral-300 hover:bg-neutral-50"}`}
              >{f.label}</button>
            ))}
          </div>
          <div className="flex-1" />
          <button
            type="button"
            data-testid="pr-export-btn"
            onClick={exportXlsx}
            disabled={exporting}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[#002FA7] text-[#002FA7] bg-white hover:bg-[#002FA7]/5 disabled:opacity-50"
          >
            <DownloadSimple size={16} weight="bold" /> {exporting ? "Esporto…" : "Esporta Excel"}
          </button>
          <button
            type="button"
            data-testid="pr-new-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC]"
          >
            <Plus size={16} weight="bold" /> Nuova richiesta
          </button>
        </div>

        <div className="bg-white border border-neutral-200 p-3">
          <div className="flex items-center gap-2 mb-3">
            <Funnel size={14} className="text-neutral-500" />
            <span className="label-eyebrow text-neutral-600">Filtri</span>
            <button data-testid="pr-filters-reset" onClick={resetFilters}
              className="ml-auto text-xs text-neutral-500 hover:text-[#002FA7] hover:underline">Azzera</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs">
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow text-neutral-500">Dal</span>
              <input data-testid="pr-filter-date-from" type="date" value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-2 py-1.5 border border-neutral-300 text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow text-neutral-500">Al</span>
              <input data-testid="pr-filter-date-to" type="date" value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-2 py-1.5 border border-neutral-300 text-sm" />
            </label>
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow text-neutral-500">Urgenza</span>
              <select data-testid="pr-filter-urgency" value={urgencyFilter}
                onChange={(e) => setUrgencyFilter(e.target.value)}
                className="px-2 py-1.5 border border-neutral-300 text-sm">
                <option value="">Tutte</option>
                <option value="bassa">Bassa</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
              </select>
            </label>
            <div className="flex flex-col gap-1">
              <span className="label-eyebrow text-neutral-500">Fornitore</span>
              <SearchableSelect testid="pr-filter-supplier" value={supplierFilter}
                options={supplierFilterOpts} onChange={setSupplierFilter}
                placeholder="Tutti i fornitori" />
            </div>
            <div className="flex flex-col gap-1">
              <span className="label-eyebrow text-neutral-500">Richiedente</span>
              <SearchableSelect testid="pr-filter-requester" value={requesterFilter}
                options={requesterOpts} onChange={setRequesterFilter}
                placeholder="Tutti i richiedenti" />
            </div>
            <label className="flex flex-col gap-1">
              <span className="label-eyebrow text-neutral-500">Ricerca libera</span>
              <input data-testid="pr-filter-text" type="search" value={textFilter}
                onChange={(e) => setTextFilter(e.target.value)}
                placeholder="N°, prodotto, note…"
                className="px-2 py-1.5 border border-neutral-300 text-sm" />
            </label>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3">
          <SumCard label="Richieste filtrate" value={filteredTotals.count} testid="pr-total-count" />
          <SumCard label="Quantità totale" value={filteredTotals.quantity.toLocaleString("it-IT")} testid="pr-total-qty" />
          <SumCard label="Importo totale" value={eur(filteredTotals.amount)} strong testid="pr-total-amount" />
        </div>
      </div>

      <div className="border border-neutral-200 bg-white overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-neutral-500 text-sm">Caricamento…</div>
        ) : filteredRows.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">
            <ShoppingCart size={30} className="mx-auto mb-2 opacity-40" />
            Nessuna richiesta corrisponde ai filtri selezionati.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-neutral-50 border-b border-neutral-200 text-neutral-600 uppercase text-[11px] tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">N° RDA</th>
                <th className="px-4 py-3 text-left">Stato</th>
                <th className="px-4 py-3 text-left">Prodotti</th>
                <th className="px-4 py-3 text-right">Q.tà totale</th>
                <th className="px-4 py-3 text-right">Importo</th>
                <th className="px-4 py-3 text-left">Fornitore</th>
                <th className="px-4 py-3 text-left">Urgenza</th>
                <th className="px-4 py-3 text-left">Richiesto da</th>
                <th className="px-4 py-3 text-left">Data</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody data-testid="pr-rows">
              {filteredRows.map((r) => (
                <tr key={r.id} data-testid={`pr-row-${r.id}`}
                    onClick={() => openDetail(r.id)}
                    className="border-b border-neutral-100 hover:bg-[#002FA7]/5 cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs" data-testid={`pr-number-${r.id}`}>
                    {r.pr_number || r.id.slice(0, 8)}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] uppercase tracking-wider ${STATUS_STYLE[r.status]}`}>
                      {r.status === "pending" && <Clock size={11} weight="bold" />}
                      {r.status === "approved" && <CheckCircle size={11} weight="bold" />}
                      {r.status === "rejected" && <XCircle size={11} weight="bold" />}
                      {STATUS_LABEL[r.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{summarizeItems(r.items)}</div>
                    {r.items && r.items.length > 1 && (
                      <div className="text-xs text-neutral-500">{r.items.length} righe</div>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-right">{r.total_quantity ?? 0}</td>
                  <td className="px-4 py-3 font-mono text-right font-semibold" data-testid={`pr-amount-${r.id}`}>{eur(r.total_amount)}</td>
                  <td className="px-4 py-3 text-neutral-700">{r.supplier_name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 border text-[11px] uppercase tracking-wider ${URGENCY_STYLE[r.urgency]}`}>
                      {r.urgency}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-neutral-700">
                    <div>{r.created_by_name}</div>
                    <div className="text-xs text-neutral-500">{r.created_by}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-neutral-500">{new Date(r.created_at).toLocaleString("it-IT")}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={(e) => { e.stopPropagation(); openDetail(r.id); }}
                      className="text-[#002FA7] text-xs hover:underline">Dettagli →</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CREATE MODAL */}
      {showCreate && (
        <RDAFormModal
          products={productOpts}
          suppliers={supplierOpts}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}

      {editing && (
        <RDAFormModal
          products={productOpts}
          suppliers={supplierOpts}
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setDetail(null); load(); }}
        />
      )}

      {/* DETAIL MODAL */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-neutral-300" onClick={(e) => e.stopPropagation()}
               data-testid="pr-detail-modal">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
              <div>
                <div className="label-eyebrow text-[#002FA7]">Richiesta di acquisto</div>
                <div className="font-mono text-xs text-neutral-500" data-testid="pr-detail-number">
                  {detail.pr_number || detail.id.slice(0, 8)}
                </div>
              </div>
              <button onClick={() => setDetail(null)} className="p-1 hover:bg-neutral-200"><XIcon size={20} /></button>
            </div>
            <div className="p-4 space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 border text-[11px] uppercase tracking-wider ${STATUS_STYLE[detail.status]}`}>
                  {STATUS_LABEL[detail.status]}
                </span>
                <span className={`px-2 py-0.5 border text-[11px] uppercase tracking-wider ${URGENCY_STYLE[detail.urgency]}`}>
                  Urgenza {detail.urgency}
                </span>
              </div>

              <div>
                <div className="label-eyebrow text-neutral-600 mb-1">Prodotti richiesti</div>
                <div className="border border-neutral-200">
                  <table className="w-full text-xs">
                    <thead className="bg-neutral-50 border-b border-neutral-200">
                      <tr>
                        <th className="px-2 py-1.5 text-left text-neutral-600">SKU</th>
                        <th className="px-2 py-1.5 text-left text-neutral-600">Prodotto</th>
                        <th className="px-2 py-1.5 text-left text-neutral-600">Cod. Produttore</th>
                        <th className="px-2 py-1.5 text-right text-neutral-600">Q.tà</th>
                        <th className="px-2 py-1.5 text-right text-neutral-600">Prezzo</th>
                        <th className="px-2 py-1.5 text-right text-neutral-600">Totale</th>
                      </tr>
                    </thead>
                    <tbody data-testid="pr-detail-items">
                      {(detail.items || []).map((it, i) => (
                        <tr key={i} className="border-b border-neutral-100 last:border-b-0">
                          <td className="px-2 py-1.5 font-mono">{it.product_sku || "?"}</td>
                          <td className="px-2 py-1.5">{it.product_name || "?"}</td>
                          <td className="px-2 py-1.5 font-mono text-neutral-500">{it.product_manufacturer_code || "—"}</td>
                          <td className="px-2 py-1.5 font-mono text-right">{it.quantity} {it.product_unit_of_measure || "pz"}</td>
                          <td className="px-2 py-1.5 font-mono text-right">{eur(it.unit_price)}</td>
                          <td className="px-2 py-1.5 font-mono text-right">{eur(it.line_total)}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-neutral-300 bg-neutral-50">
                        <td colSpan={3} className="px-2 py-1.5 text-right font-semibold uppercase tracking-wider text-neutral-600">Totale</td>
                        <td className="px-2 py-1.5 font-mono text-right font-semibold">{detail.total_quantity ?? 0}</td>
                        <td></td>
                        <td className="px-2 py-1.5 font-mono text-right font-semibold text-[#002FA7]" data-testid="pr-detail-total-amount">{eur(detail.total_amount)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><div className="label-eyebrow text-neutral-600">Fornitore</div><div>{detail.supplier_name || "—"}</div></div>
                <div><div className="label-eyebrow text-neutral-600">Richiesto da</div><div>{detail.created_by_name}<div className="text-xs text-neutral-500">{detail.created_by}</div></div></div>
                <div className="col-span-2"><div className="label-eyebrow text-neutral-600">Data</div><div className="text-xs">{new Date(detail.created_at).toLocaleString("it-IT")}</div></div>
              </div>
              {detail.notes && (
                <div><div className="label-eyebrow text-neutral-600">Note del richiedente</div><div className="whitespace-pre-wrap">{detail.notes}</div></div>
              )}
              {detail.status !== "pending" && detail.reviewed_by && (
                <div className="pt-2 border-t border-neutral-200">
                  <div className="label-eyebrow text-neutral-600">Revisionata da</div>
                  <div>{detail.reviewed_by_name} — {new Date(detail.reviewed_at).toLocaleString("it-IT")}</div>
                  {detail.review_notes && (
                    <div className="mt-2"><div className="label-eyebrow text-neutral-600">Note del revisore</div><div className="whitespace-pre-wrap">{detail.review_notes}</div></div>
                  )}
                </div>
              )}
              {isAdmin && detail.status === "pending" && (
                <div className="pt-3 border-t border-neutral-200 space-y-2">
                  <div className="label-eyebrow text-[#002FA7]">Revisione admin</div>
                  <textarea
                    data-testid="pr-review-notes"
                    rows={3}
                    value={reviewNotes}
                    onChange={(e) => setReviewNotes(e.target.value)}
                    placeholder="Note del revisore (opzionale, incluse nell'email al richiedente)"
                    className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                  />
                  <div className="flex gap-2">
                    <button
                      data-testid="pr-approve-btn"
                      disabled={processing}
                      onClick={() => review("approve")}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40"
                    >
                      <CheckCircle size={16} weight="bold" /> Approva
                    </button>
                    <button
                      data-testid="pr-reject-btn"
                      disabled={processing}
                      onClick={() => review("reject")}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-red-600 text-white hover:bg-red-700 disabled:opacity-40"
                    >
                      <XCircle size={16} weight="bold" /> Rifiuta
                    </button>
                  </div>
                </div>
              )}
              {(detail.status === "pending" && (detail.created_by_id === user?.id || isAdmin)) && (
                <div className="pt-3 border-t border-neutral-200 flex items-center gap-3">
                  <button
                    data-testid="pr-edit-btn"
                    onClick={() => { setEditing(detail); setDetail(null); }}
                    className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1"
                  ><PencilSimple size={12} /> Modifica richiesta</button>
                  <button
                    data-testid="pr-delete-btn"
                    onClick={() => remove(detail)}
                    className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                  ><Trash size={12} /> Elimina richiesta</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SumCard({ label, value, strong, testid }) {
  return (
    <div className="bg-white border border-neutral-200 p-3">
      <div className="label-eyebrow mb-1 text-neutral-600">{label}</div>
      <div data-testid={testid} className={`font-mono ${strong ? "text-xl md:text-2xl font-bold text-[#002FA7]" : "text-lg font-semibold"}`}>{value}</div>
    </div>
  );
}

function RDAFormModal({ products, suppliers, initial, onClose, onSaved }) {
  const isEdit = !!initial;
  const emptyItem = () => ({ product_id: "", quantity: 1 });
  const [items, setItems] = useState(
    isEdit && initial.items?.length
      ? initial.items.map((it) => ({ product_id: it.product_id, quantity: it.quantity }))
      : [emptyItem()]
  );
  const [notes, setNotes] = useState(initial?.notes || "");
  const [supplierId, setSupplierId] = useState(initial?.supplier_id || "");
  const [urgency, setUrgency] = useState(initial?.urgency || "media");
  const [saving, setSaving] = useState(false);

  const updateItem = (idx, patch) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const addItem = () => setItems((prev) => [...prev, emptyItem()]);
  const removeItem = (idx) => setItems((prev) => prev.length === 1 ? prev : prev.filter((_, i) => i !== idx));
  const [estimate, setEstimate] = useState({ items: [], total_amount: 0 });

  useEffect(() => {
    const valid = items
      .map((it) => ({ product_id: it.product_id, quantity: parseInt(it.quantity, 10) || 0 }))
      .filter((it) => it.product_id && it.quantity > 0);
    if (!valid.length) { setEstimate({ items: [], total_amount: 0 }); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      api.post("/purchase-requests/estimate", { items: valid, supplier_id: supplierId || null })
        .then((r) => { if (!cancelled) setEstimate(r.data); })
        .catch((e) => { if (!cancelled) toast.error(formatApiError(e)); });
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [items, supplierId]);
  const lineFor = (it) => estimate.items.find((e) => e.product_id === it.product_id);

  const submit = async () => {
    const cleaned = items
      .map((it) => ({ product_id: (it.product_id || "").trim(), quantity: parseInt(it.quantity, 10) || 0 }))
      .filter((it) => it.product_id && it.quantity > 0);
    if (!cleaned.length) { toast.error("Aggiungi almeno un prodotto con quantità valida"); return; }
    // De-duplicate: same product entered multiple times → sum quantities
    const merged = new Map();
    for (const it of cleaned) {
      merged.set(it.product_id, (merged.get(it.product_id) || 0) + it.quantity);
    }
    const payloadItems = Array.from(merged, ([product_id, quantity]) => ({ product_id, quantity }));

    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/purchase-requests/${initial.id}`, {
          items: payloadItems,
          notes: notes.trim() || null,
          supplier_id: supplierId || null,
          urgency,
        });
        toast.success("Richiesta aggiornata");
      } else {
        await api.post("/purchase-requests", {
          items: payloadItems,
          notes: notes.trim() || null,
          supplier_id: supplierId || null,
          urgency,
        });
        toast.success(`Richiesta inoltrata (${payloadItems.length} ${payloadItems.length === 1 ? "prodotto" : "prodotti"})`);
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white max-w-2xl w-full max-h-[92vh] overflow-y-auto border border-neutral-300"
           onClick={(e) => e.stopPropagation()} data-testid={isEdit ? "pr-edit-modal" : "pr-create-modal"}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 bg-neutral-50">
          <div className="font-display font-bold tracking-tight">
            {isEdit ? `Modifica ${initial.pr_number || "Richiesta"}` : "Nuova Richiesta di Acquisto"}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-neutral-200"><XIcon size={20} /></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="label-eyebrow text-neutral-700">Prodotti richiesti *</label>
              <button
                type="button"
                data-testid="pr-add-item-btn"
                onClick={addItem}
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs border border-[#002FA7] text-[#002FA7] hover:bg-[#002FA7]/5"
              >
                <Plus size={14} weight="bold" /> Aggiungi prodotto
              </button>
            </div>
            <div className="space-y-2">
              {items.map((it, idx) => (
                <div key={idx} data-testid={`pr-item-row-${idx}`}
                     className="flex items-start gap-2 p-2 border border-neutral-200 bg-neutral-50/50">
                  <div className="flex-1 min-w-0">
                    <SearchableSelect
                      testid={`pr-item-product-${idx}`}
                      value={it.product_id}
                      options={products}
                      onChange={(v) => updateItem(idx, { product_id: v })}
                      placeholder="Seleziona prodotto…"
                    />
                    {lineFor(it) && (
                      <div className="text-[11px] text-neutral-500 mt-1 font-mono" data-testid={`pr-item-price-${idx}`}>
                        {eur(lineFor(it).unit_price)} cad. ({lineFor(it).price_source === "listino" ? "listino" : "prezzo prodotto"}) · riga {eur(lineFor(it).unit_price * (parseInt(it.quantity, 10) || 0))}
                      </div>
                    )}
                  </div>
                  <input
                    data-testid={`pr-item-quantity-${idx}`}
                    type="number" min="1"
                    value={it.quantity}
                    onChange={(e) => updateItem(idx, { quantity: e.target.value })}
                    className="w-24 px-2 py-2 border border-neutral-300 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
                    placeholder="Q.tà"
                  />
                  <button
                    type="button"
                    data-testid={`pr-item-remove-${idx}`}
                    onClick={() => removeItem(idx)}
                    disabled={items.length === 1}
                    title={items.length === 1 ? "Serve almeno un prodotto" : "Rimuovi prodotto"}
                    className="p-2 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label-eyebrow text-neutral-700">Urgenza</label>
              <select
                data-testid="pr-urgency"
                value={urgency}
                onChange={(e) => setUrgency(e.target.value)}
                className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
              >
                <option value="bassa">Bassa</option>
                <option value="media">Media</option>
                <option value="alta">Alta</option>
              </select>
            </div>
            <div>
              <label className="label-eyebrow text-neutral-700">Fornitore preferito</label>
              <SearchableSelect
                testid="pr-supplier-select"
                value={supplierId}
                options={suppliers}
                onChange={setSupplierId}
                placeholder="— Usa fornitore del prodotto —"
              />
            </div>
          </div>

          <div>
            <label className="label-eyebrow text-neutral-700">Note</label>
            <textarea
              data-testid="pr-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Motivazione / dettagli aggiuntivi"
              className="w-full px-3 py-2 border border-neutral-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]"
            />
          </div>

          <div className="flex items-center justify-between p-3 border border-[#002FA7]/30 bg-[#002FA7]/5">
            <span className="label-eyebrow text-[#002FA7]">Importo stimato richiesta</span>
            <span className="font-mono text-lg font-bold text-[#002FA7]" data-testid="pr-estimate-total">{eur(estimate.total_amount)}</span>
          </div>

          <div className="flex gap-2 pt-2">
            <button
              data-testid="pr-submit"
              disabled={saving}
              onClick={submit}
              className="flex-1 px-4 py-2 bg-[#002FA7] text-white hover:bg-[#0038CC] disabled:opacity-40"
            >
              {saving ? (isEdit ? "Salvataggio…" : "Invio…") : (isEdit ? "Salva modifiche" : "Invia richiesta")}
            </button>
            <button onClick={onClose} className="px-4 py-2 border border-neutral-300 hover:bg-neutral-100">Annulla</button>
          </div>
        </div>
      </div>
    </div>
  );
}
